/**
 * ClassAttendance — owner/admin view of attendance taken by coaches.
 *
 * Reads `GET /api/v1/check-in/class/{id}/status`, which already permits
 * ADMIN and OWNER and returns a denormalized roster (child name, group,
 * parent contact, checked-in flag and timestamp).
 *
 * Why check-ins and not the `attendance` table: coaches take attendance
 * exclusively through the check-in screen. Nothing in the app ever calls
 * `POST /attendance/mark`, so the `attendances` table is effectively empty —
 * a view built on it would show zeros for every class. This reads what the
 * coaches actually record.
 *
 * Like the coach pages, the picker offers "All memberships" sheets (see
 * utils/classGroups): one list for every membership of a TDC site or of a
 * school that runs one program on several days.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Download, Users, XCircle } from "lucide-react";
import Header from "../../components/Header";
import FilterBar from "../../components/admin/FilterBar";
import checkinService from "../../api/services/checkin.service";
import classesService from "../../api/services/classes.service";
import toast from "react-hot-toast";
import useClassGroups from "../../hooks/useClassGroups";
import { classesOf } from "../../utils/classGroups";

import { sortClassesByName } from "../../utils/format";
/** Local YYYY-MM-DD — never via toISOString(), which shifts the day east of UTC. */
function todayLocalISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function ClassAttendance() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(todayLocalISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const classGroups = useClassGroups(classes);
  const selection = useMemo(
    () => [...classGroups, ...classes].find((c) => c.id === classId),
    [classGroups, classes, classId],
  );
  const isGroup = Boolean(selection?.isGroup);
  // A stable key, so a regrouping that changes nothing does not refetch
  const memberIds = classesOf(selection, date).map((c) => c.id).join(",");

  useEffect(() => {
    (async () => {
      try {
        const res = await classesService.getAll();
        const items = sortClassesByName(res?.items || res || []);
        setClasses(items);
        if (items.length && !classId) setClassId(items[0].id);
      } catch (e) {
        console.error("Failed to load classes:", e);
        toast.error("Could not load classes");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!memberIds) return;
    setLoading(true);
    try {
      const members = classesOf(selection, date);
      const rosters = await Promise.all(
        members.map(async (c) =>
          (await checkinService.getClassStatus(c.id, date)).map((r) => ({
            ...r,
            class_name: c.name,
          })),
        ),
      );
      setRows(rosters.flat());
    } catch (e) {
      console.error("Failed to load attendance:", e);
      toast.error(e?.message || "Could not load attendance");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIds, date]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.child_name || ""} ${r.parent_name || ""}`.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const present = filtered.filter((r) => r.is_checked_in).length;
  const total = filtered.length;
  const rate = total ? Math.round((present / total) * 100) : 0;
  const selectedClass = selection;

  const exportCsv = () => {
    const header = "Child,Membership,Group,Present,Checked in at,Parent,Phone\n";
    const body = filtered
      .map((r) =>
        [
          r.child_name || "",
          r.class_name || "",
          r.group_number ?? "",
          r.is_checked_in ? "Yes" : "No",
          r.is_checked_in ? formatTime(r.checked_in_at) : "",
          r.parent_name || "",
          r.parent_phone || "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([header + body], { type: "text/csv;charset=utf-8;" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${selectedClass?.name || classId}-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <Header />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold font-manrope text-[#173151]">
              Class Attendance
            </h1>
            <p className="text-xs sm:text-sm font-manrope text-[#666D80] mt-1">
              Check-ins recorded by coaches.
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-manrope font-semibold text-[#173151] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Selectors */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <select
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:ring-2 focus:ring-[#F3BC48] focus:border-[#F3BC48] min-w-[220px]"
          >
            {classGroups.length > 0 && (
              <optgroup label="All memberships">
                {classGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Classes">
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
          <div className="relative">
            <CalendarDays className="w-4 h-4 text-[#666D80] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:ring-2 focus:ring-[#F3BC48] focus:border-[#F3BC48]"
            />
          </div>
        </div>

        <FilterBar
          searchValue={search}
          onSearch={setSearch}
          searchPlaceholder="Search child or parent…"
        />

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 my-4">
          {[
            { label: "Present", value: present, icon: CheckCircle2, tone: "text-green-600 bg-green-50" },
            { label: "Absent", value: total - present, icon: XCircle, tone: "text-red-600 bg-red-50" },
            { label: "Attendance", value: `${rate}%`, icon: Users, tone: "text-[#173151] bg-yellow-50" },
          ].map(({ label, value, icon: Icon, tone }) => (
            <div key={label} className={`rounded-lg p-3 sm:p-4 ${tone}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-xs sm:text-sm font-manrope">{label}</span>
              </div>
              <p className="text-lg sm:text-2xl font-semibold font-manrope mt-1">
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* Roster */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Child", ...(isGroup ? ["Membership"] : []), "Group", "Status", "Checked in", "Parent"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-xs font-manrope font-semibold text-[#666D80] uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isGroup ? 6 : 5} className="px-4 py-8 text-center text-sm font-manrope text-[#666D80]">
                    Loading…
                  </td>
                </tr>
              ) : !filtered.length ? (
                <tr>
                  <td colSpan={isGroup ? 6 : 5} className="px-4 py-8 text-center text-sm font-manrope text-[#666D80]">
                    No students on this roster for {date}.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.enrollment_id || r.child_id} className="border-b border-gray-100">
                    <td className="px-4 py-3 text-sm font-manrope text-[#173151]">
                      {r.child_name || "—"}
                    </td>
                    {isGroup && (
                      <td className="px-4 py-3 text-sm font-manrope text-[#666D80]">
                        {r.class_name}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-manrope text-[#666D80]">
                      {r.group_number ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-manrope font-semibold ${
                          r.is_checked_in
                            ? "text-green-700 bg-green-50"
                            : "text-red-700 bg-red-50"
                        }`}
                      >
                        {r.is_checked_in ? "Present" : "Absent"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-manrope text-[#666D80]">
                      {r.is_checked_in ? formatTime(r.checked_in_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm font-manrope text-[#666D80]">
                      {r.parent_name || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
